import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { minsToHhMm } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyMeditationUserDetail } from './companyMeditationApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_GRID_NO_X,
  toneChip,
  paletteAt
} from '../../../../component/common/designSystem';
import { formatChipClass } from './meditationTheme';

/**
 * Meditation Sounds → User profile.
 * One user's practice: totals, the meditations they return to, and their most
 * recent plays. `fetcher`/`backPath`/`backLabel` are props so the super-admin
 * Meditation Hub can reuse the page against the unscoped endpoint.
 */
const MeditationUserDetail = ({
  fetcher = fetchCompanyMeditationUserDetail,
  backPath,
  backLabel
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const topMeditations = detail?.topMeditations || [];
  const history = detail?.meditationHistory || [];

  const topOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topMeditations.map((entry, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topMeditations.map((entry) => entry.meditation) },
    tooltip: { y: { formatter: (val) => `${val} plays` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.plays}
          title="Total Plays"
          value={user ? user.totalPlays : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Listening Time"
          value={user ? minsToHhMm(user.totalListenTimeMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.audioLibrary}
          title="Meditations Played"
          value={user ? user.meditationsPlayed : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Avg Session"
          value={user ? `${user.avgSessionMins} min` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Top Meditation"
          value={detail?.mostPlayedMeditation || '-'}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Top Meditations" subtitle="This user's most played">
          {topMeditations.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[{ name: 'Plays', data: topMeditations.map((entry) => entry.plays) }]}
              options={topOptions}
            />
          ) : (
            <PanelEmpty>No meditation activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Listening History" subtitle="Most recent meditations played">
          {history.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Meditation</th>
                  <th className="py-2 pr-4 font-medium">Format</th>
                  <th className="py-2 pr-4 font-medium text-right">Plays</th>
                  <th className="py-2 font-medium text-right">Listened</th>
                </>
              }
            >
              {history.map((entry, index) => (
                <tr key={`${entry.date}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{entry.date}</td>
                  <td className="py-2 pr-4">{entry.meditation}</td>
                  <td className="py-2 pr-4">
                    {entry.format ? (
                      <Chip className={formatChipClass(entry.format)}>{entry.format}</Chip>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right">{entry.plays}</td>
                  <td className="py-2 text-right">{minsToHhMm(entry.listenMins)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No listening history yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

MeditationUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string.isRequired,
  backLabel: PropTypes.string
};

MeditationUserDetail.defaultProps = {
  backLabel: 'Back'
};

export default MeditationUserDetail;
